import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fetchAllPages, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * The dispatcher's side of the B2B portal: client companies, their sign-in
 * links, and the approval queue.
 *
 * The queue is NOT polled on a timer. Every request that lands publishes a
 * `client_request_submitted` SSE event, which hooks/useLiveSync.js maps onto
 * the `order-requests` cache — so the badge updates the instant a client
 * presses send, and an idle console makes no requests at all. A poll here
 * would be both slower and busier.
 */

export function useOrderRequests(status = 'pending_approval') {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.orderRequests(tenantId, status),
    queryFn: () => api(`/api/order-requests?status=${encodeURIComponent(status)}`),
    enabled: Boolean(tenantId),
    placeholderData: [],
  });
}

/** Just the count, for the navigation badge. Shares the pending query's
 *  cache entry, so showing the badge costs no extra request. */
export function usePendingRequestCount() {
  const { data = [] } = useOrderRequests('pending_approval');
  return data.length;
}

/**
 * V-86. What this client owes, for the screens where more work is agreed.
 *
 * A separate request rather than a field on the request payload, because
 * the order form asks the same question about a client it reaches through a
 * site — one hook, one cache entry, two callers, and the answer is shared
 * when both are open.
 *
 * Not polled and deliberately short-lived: payment is recorded by hand in
 * this product, so the figure moves when a person marks an invoice paid,
 * and a dispatcher who has just done that must not be warned about money
 * that arrived a minute ago.
 */
export function useClientBalance(clientId) {
  return useQuery({
    queryKey: qk.clientBalance(clientId),
    queryFn: () => api(`/api/clients/${clientId}/balance`),
    enabled: Boolean(clientId),
    staleTime: 30_000,
  });
}

export function useApproveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, ...body }) =>
      api(`/api/order-requests/${requestId}/approve`, { method: 'POST', body }),
    // Approval creates orders, and may create a delivery site — invalidate
    // everything it can have touched rather than trusting the SSE round trip
    // to arrive before the dispatcher looks at the orders tab.
    onSuccess: () => {
      ['order-requests', 'orders', 'dashboard', 'sites', 'clients'].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
  });
}

export function useRejectRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, rejectionReason }) =>
      api(`/api/order-requests/${requestId}/reject`, {
        method: 'POST',
        body: { rejection_reason: rejectionReason },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order-requests'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useClients({ includeInactive = false, search, limit, offset, segment } = {}) {
  return useClientQuery({ includeInactive, search, limit, offset, segment, all: false });
}

/**
 * Every client, paged through to the end — the same split useDirectory.js
 * draws between useQuarries() and useAllQuarries(), and for the same
 * reason.
 *
 * A picker must be offered the whole list. core/paging.py serves 25 rows by
 * default and caps a page at 100, so a bare useClients() behind a <select>
 * silently stops at the 26th customer — and a client missing from the
 * invoicing screen's picker looks exactly like a client who cannot be
 * invoiced. One request for any fleet that fits in a page, which is most of
 * them.
 */
export function useAllClients({ includeInactive = false, search } = {}) {
  return useClientQuery({ includeInactive, search, all: true });
}

function useClientQuery({ includeInactive, search, limit, offset, segment, all }) {
  const tenantId = useTenantId();
  const path = `/api/tenants/${tenantId}/clients`;
  const filters = { include_inactive: includeInactive ? '1' : undefined, search, segment: segment || undefined };

  const query = useQuery({
    queryKey: qk.clients(tenantId, { includeInactive, search, limit, offset, segment, all }),
    queryFn: () =>
      all ? fetchAllPages(path, filters) : api(`${path}${qs({ ...filters, limit, offset })}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    placeholderData: { data: [], total_count: 0 },
  });

  return { ...query, data: query.data?.data ?? [], totalCount: query.data?.total_count ?? 0 };
}

export function useCreateClient() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api(`/api/tenants/${tenantId}/clients`, { method: 'POST', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, ...body }) =>
      api(`/api/clients/${clientId}`, { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

/**
 * Issues a sign-in link and hands the URL back to the caller.
 *
 * Nothing is emailed: this deployment has no mail transport, so the
 * dispatcher copies the link and sends it over whichever channel they
 * already use with that client. The UI says so plainly rather than showing
 * a "Sent!" toast for a message that was never sent.
 *
 * Deliberately no cache invalidation — a link is a one-shot credential, not
 * state any view displays. The returned token exists only in this response.
 */
export function useIssueMagicLink() {
  return useMutation({
    mutationFn: ({ clientId, ttlMinutes }) =>
      api(`/api/clients/${clientId}/magic-link`, {
        method: 'POST',
        body: ttlMinutes ? { ttl_minutes: ttlMinutes } : {},
      }),
  });
}

export function useRevokeClientSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId }) =>
      api(`/api/clients/${clientId}/revoke-sessions`, { method: 'POST', body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clients'] }),
  });
}

/**
 * V-71. Archiving a client, and the way back.
 *
 * DELETE /api/clients/<id> is a soft delete — the same pattern drivers,
 * quarries and sites use, so a two-year-old order still resolves to a client
 * name. It also revokes every live portal session, which is the half that
 * matters on the day a carrier stops working with somebody: `active` is
 * re-read on every portal request (core/client_auth.resolve_client_session),
 * so the client stops being able to place order requests immediately rather
 * than whenever their 30-day token happens to lapse.
 *
 * Neither this route nor the restore below had a caller anywhere in
 * frontend/src, so the console's client list could only ever grow, and a row
 * archived by any other means — the API, an earlier install — was invisible
 * on every screen with no way back.
 *
 * The invoicing caches go with the client caches: core/invoicing.receivables
 * reads the invoices table and never joins clients, so an archived client's
 * debt stays on the receivables screen exactly as it was. That is deliberate
 * and the screen says so — but the lists still have to refetch, or the same
 * client reads as archived in one panel and live in the next.
 */
export function useArchiveClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId }) => api(`/api/clients/${clientId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

/** Mirror of useRestoreSite in useDataAdmin.js: `active: true` also clears
 *  the backend's deleted_at stamp (core/archive.py), so a client archived
 *  again later reads as archived on the second date, not the first. */
export function useRestoreClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId }) =>
      api(`/api/clients/${clientId}`, { method: 'PUT', body: { active: true } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}
